<template>
  <a-row justify="center" align="middle" style="min-height: 100vh">
    <a-col :xs="22" :sm="16" :md="12" :lg="8">
      <a-card bordered>
        <h1 class="text-center">{{ t("title.login") }}</h1>
        <a-form layout="vertical" :model="loginForm" :rules="rules" ref="form">
          <a-form-item :label="t('form.label.email')" name="email" required>
            <a-input
              v-model:value="loginForm.email"
              :placeholder="t('form.placeholder.email')"
              autocomplete="email"
              allow-clear
            />
          </a-form-item>

          <a-form-item
            :label="t('form.label.password')"
            name="password"
            required
          >
            <a-input-password
              v-model:value="loginForm.password"
              :placeholder="t('form.placeholder.password')"
              autocomplete="current-password"
              allow-clear
            />
          </a-form-item>
          <a-row class="privacy-box">
            <a-form-item name="checkFlag" required>
              <a-checkbox v-model:checked="loginForm.checkFlag">
                <span class="box-text"
                  >{{ t("form.checkbox.agree")
                  }}<a :href="termsOfUseLink" target="_blank">{{
                    t("form.checkbox.termsOfUse")
                  }}</a
                  >{{ t("form.checkbox.and")
                  }}<a :href="termsOfPrivacyLink" target="_blank">{{
                    t("form.checkbox.privacyPolicy")
                  }}</a></span
                >
              </a-checkbox>
              <br />
            </a-form-item>
          </a-row>

          <div class="action-links">
            <a @click="switchToForgotPassword" class="text-danger">
              {{ t("title.forgetPassword") }}
            </a>
          </div>
          <div class="action-links">
            <span @click="navigateToCreateAccount" class="account-style">
              {{ t("title.createAccount") }}
            </span>
          </div>

          <a-button type="primary" block @click="onSubmit" class="mt-2">
            {{ t("button.login") }}
          </a-button>
        </a-form>
      </a-card>
    </a-col>
  </a-row>
</template>

<script setup>
import { reactive, ref, onMounted, computed } from "vue";
import { message } from "ant-design-vue";
import { useRouter } from "vue-router";
import CryptoJS from "crypto-js";
import { login } from "@/api/user";
import { getMenu } from "@/api/menu";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex"; // 导入 useStore 以访问 Vuex
import { getToken } from "@/utils/auth";
import { getOfficialWebsiteSource } from "@/utils/config";
import { getLocalizedPath } from "@/utils/router";
const { t } = useI18n();
const router = useRouter();
const officialWebsiteSource = ref(getOfficialWebsiteSource());
const termsOfUseLink = ref(null);
const termsOfPrivacyLink = ref(null);
const store = useStore(); // 使用 useStore 来获取 Vuex store 实例
const loginForm = reactive({
  email: "",
  password: "",
  checkFlag: undefined,
});
const form = ref(null);

const rules = computed(() => {
  return {
    email: [
      {
        required: true,
        message: t("form.validationEmailRequired"),
        trigger: "blur",
      },
      {
        type: "email",
        message: t("form.validationEmailInvalid"),
        trigger: "blur",
      },
    ],
    password: [
      {
        required: true,
        message: t("form.validationPasswordRequired"),
        trigger: "blur",
      },
      {
        pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,20}$/,
        message: t("form.password.validate"),
        trigger: "blur",
      },
    ],
    checkFlag: [
      {
        required: true,
        message: t("form.placeholder.privacy"),
        trigger: "change",
      },
      {
        validator: (rule, value) => {
          if (value === false) {
            return Promise.reject(new Error(t("form.placeholder.privacy")));
          } else {
            return Promise.resolve();
          }
        },
      },
    ],
  };
});
onMounted(() => {
  termsOfUseLink.value =
    officialWebsiteSource.value && officialWebsiteSource.value === "elite"
      ? "https://sunseekerelite.com/terms-of-use/"
      : officialWebsiteSource.value && officialWebsiteSource.value === "tech"
      ? "https://sunseekertech.com/terms-of-use/"
      : "";
  termsOfPrivacyLink.value =
    officialWebsiteSource.value && officialWebsiteSource.value === "elite"
      ? "https://sunseekerelite.com/privacy-policy/"
      : officialWebsiteSource.value && officialWebsiteSource.value === "tech"
      ? "https://sunseekertech.com/privacy-policy/"
      : "";
});
// 密码加密
function encryptPassword(password) {
  return CryptoJS.SHA256(password).toString();
}

// 提交登录
const onSubmit = async () => {
  form.value
    ?.validate()
    .then(() => {
      try {
        // 调用登录接口
        login({
          username: loginForm.email,
          password: loginForm.password,
          grantType: "password",
          scope: "server",
        })
          .then((data) => {
            // 存储用户信息
            store.dispatch("auth/saveUser", {
              data,
              username: loginForm.email,
              password: loginForm.password,
            });
            store.dispatch("auth/saveToken", data?.access_token);
            // 调用菜单权限接口,目前没有菜单权限控制，则直接根据项目配置路由菜单进行跳转
            // 获取当前语言参数
            // 构造带语言参数的跳转路径
            const firstMenuPath = getLocalizedPath("/warranty"); // 根据实际菜单配置调整

            // 优先跳转到上次访问页面
            const redirectPath =
              router.currentRoute.value.query.redirect || firstMenuPath;
            router.push(redirectPath);
            // router.push({
            //   name: "Warranty",
            //   params: { lang: currentLang },
            // });
            // getMenu({
            //   headers: {
            //     Authorization: `Bearer ${data.token}`,
            //   },
            // }).then((response) => {
            //   localStorage.setItem("user_menus", JSON.stringify(userMenus));

            //   // 跳转到第一个有权限的菜单页面
            //   message.success(t("message.loginSuccess"));
            // });
          })
          .catch(() => {});
      } catch (error) {
        message.error(t("message.loginFailure"));
      }
    })
    .catch(() => {
      console.log("error 校验不通过");
    });
};

// 切换到忘记密码页面
const switchToForgotPassword = () => {
  const jumpPath = getLocalizedPath("/forgetPassword");
  router.push(jumpPath);
};

// 导航到创建账户页面
const navigateToCreateAccount = () => {
  const jumpPath = getLocalizedPath("/register");
  router.push(jumpPath);
};
</script>

<style lang="less">
.text-center {
  text-align: center;
}

.mt-2 {
  margin-top: 16px;
  background-color: var(--primary-color) !important;
  box-shadow: none !important;
}

.text-danger {
  color: red;
}

.action-links {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}
.account-style {
  text-decoration: underline;
  cursor: pointer;
}
.privacy-box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  .ant-form-item {
    margin-bottom: 0px !important;
  }
  .ant-form-item-control-input-content {
    display: flex;
  }
}
.box-text {
  text-align: left !important;
  a {
    color: var(--primary-color);
    font-weight: 600;
  }
}
</style>
